import React, { useCallback, useEffect, useState } from "react";
import { graphql, navigate } from "gatsby";
import { FAQPage } from "schema-dts";

import * as styles from "./faq.module.scss";
import QueryEdgeNode from "../../models/query-edge-node.model";
import { IPageBase } from "../../models/page-base.model";
import { IQuestion } from "../../models/structured-data.model";
import usePageMetadata from "../../hooks/use-page-metadata";
import createFAQPage from "../../utils/structured-data/create-faq-page";
import useUser from "../../apps/auth/hooks/use-user";
import { getAbsolutePath } from "../../apps/utility/routes.utility";
import logoFull from "../../assets/images/logo_full.png";

import SingleFaq, { ISingleFaq, ISingleFaqProps } from "../molecules/single-faq";
import MainLayout from "../../layouts/main-layout";
import Link from "../atoms/link";
import MinimalFooter from "../organisms/minimal-footer";

export interface IFaqProps extends IPageBase {
    readonly data: {
        allFaq: QueryEdgeNode<ISingleFaq>;
    };
}

const navItems = [
    { label: "O Mnie", to: "/o-nas/" },
    { label: "Blog", to: "/blog/" },
    { label: "Kontakt", to: "/kontakt/" },
];

const Faq: React.FC<IFaqProps> = ({ data, pageContext }) => {
    const [questions, setQuestions] = useState<Record<string, IQuestion>>({});
    const [structuredData, setStructuredData] = useState<FAQPage | undefined>();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { pageTitle } = usePageMetadata(pageContext);
    const { isLoggedIn } = useUser();
    const accountLinkHref = isLoggedIn ? getAbsolutePath("ACCOUNT_DASHBOARD") : "/auth/";
    const accountLinkLabel = isLoggedIn ? "Twoje konto" : "Zaloguj się";

    const handleCreateQuestion: ISingleFaqProps["onCreateQuestion"] = useCallback(question => {
        setQuestions(previous => ({ ...previous, [question.name]: question }));
    }, []);

    useEffect(() => {
        setStructuredData(createFAQPage(Object.values(questions)));
    }, [questions]);

    return (
        <MainLayout
            HeaderComponent={null}
            footer={false}
            className={styles.faqPage}
            SEOProps={{ title: pageTitle, structuredData }}
        >
            <nav className={styles.faqNav} aria-label="Główna nawigacja">
                <Link to="/" className={styles.brand}>
                    <img src={logoFull} alt="Fashion Fairy Virtual Stylist" />
                </Link>
                <div className={styles.navLinks}>
                    {navItems.map(item => (
                        <Link key={item.to} to={item.to}>{item.label}</Link>
                    ))}
                </div>
                <Link to={accountLinkHref} className={styles.loginButton} noPrefetch>
                    {accountLinkLabel}
                </Link>
                <button
                    className={styles.menuToggle}
                    type="button"
                    aria-label={isMenuOpen ? "Zamknij menu" : "Otwórz menu"}
                    aria-expanded={isMenuOpen}
                    aria-controls="faq-mobile-menu"
                    onClick={() => setIsMenuOpen(open => !open)}
                >
                    <span /><span /><span />
                </button>
                <div
                    id="faq-mobile-menu"
                    className={`${styles.mobileMenu} ${isMenuOpen ? styles.mobileMenuOpen : ""}`}
                >
                    {navItems.map(item => (
                        <Link key={item.to} to={item.to}>{item.label}</Link>
                    ))}
                    <button
                        type="button"
                        onClick={() => {
                            setIsMenuOpen(false);
                            navigate(accountLinkHref);
                        }}
                    >
                        {accountLinkLabel}
                    </button>
                </div>
            </nav>

            <header className={styles.faqHero}>
                <p>FAQ</p>
                <h1>Masz pytania? Mamy odpowiedzi!</h1>
                <span>
                    Jeśli coś jest dla Ciebie niejasne, tutaj znajdziesz odpowiedzi
                    dotyczące Fashion Fairy i działania serwisu.
                </span>
            </header>

            <section className={styles.faqList} aria-label="Pytania i odpowiedzi">
                {data.allFaq.edges.map(({ node }, index) => (
                    <SingleFaq
                        key={`single-faq-${index}`}
                        data={node}
                        onCreateQuestion={handleCreateQuestion}
                    />
                ))}
            </section>

            <section className={styles.contactPrompt}>
                <div>
                    <h2>Nie znalazłaś odpowiedzi?</h2>
                    <p>
                        Napisz do nas. Chętnie pomożemy Ci odnaleźć styl, w którym
                        poczujesz się jak najlepsza wersja siebie. ✨
                    </p>
                </div>
                <Link to="/kontakt/">Napisz do nas</Link>
            </section>

            <MinimalFooter />
        </MainLayout>
    );
};

export const query = graphql`
    query($language: String!) {
        locales: allLocale(filter: { language: { eq: $language } }) {
            edges {
                node {
                    ns
                    data
                    language
                }
            }
        }
        allFaq {
            edges {
                node {
                    translations {
                        pl {
                            question
                            answer
                        }
                    }
                }
            }
        }
    }
`;

export default Faq;
